import React, { useState, useEffect, useContext } from 'react';
import styled, { keyframes } from 'styled-components';
import ConfettiCelebration from './ConfettiCelebration';
import { UserContext } from "../contexts/user";

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const scaleIn = keyframes`
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1.1);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
  100% {
    transform: scale(1);
  }
`;

const BadgeImage = styled.img`
  width: 120px;
  height: 120px;
  object-fit: contain;
  margin-bottom: 1rem;
`;

const Container = styled.div`
  min-height: 100vh;
  background-color: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
`;

const DemoButton = styled.button`
  padding: 0.75rem 1.5rem;
  background-color: #2563eb;
  color: white;
  border-radius: 0.5rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: background-color 0.3s ease;

  &:hover {
    background-color: #1d4ed8;
  }
`;

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const PopupContainer = styled.div`
  background: var(--hn-bg);
  border-radius: 16px;
  text-align: center;
  max-width: 700px;
  width: 90%;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3);
  border: 1px solid var(--hn-yellow);
`;

const Header = styled.div`
  // background: #534c34;
  color: #ffffff;
  padding: 1.5rem;
  border-radius: 1rem 1rem 0 0;
  text-align: center;
  border-bottom: 1px solid var(--hn-yellow);
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: bold;
  margin: 0;
`;

const Subtitle = styled.p`
  margin-top: 0.5rem;
  color: #d1fae5;
  margin-bottom: 0;
`;

const RequirementsList = styled.div`
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const RequirementItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;

  opacity: ${props => props.checked ? '1' : '0'};
  transition: all 0.5s ease;
  animation: ${props => props.checked ? fadeIn : 'none'} 0.5s ease-out;
  font-size: 18px;
`;

const Checkbox = styled.div`
  flex-shrink: 0;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  border: 2px solid #1fb910;
  background-color: #379b14;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.5s ease;

  &n:hover {
    transform: translateY(-5px);
  }
`;

const RequirementText = styled.p`
  color: #ffffffff;
  margin: 0;
  font-weight: ${props => props.checked ? '500' : '400'};
  transition: all 0.5s ease;
`;

const ButtonContainer = styled.div`
  padding: 0 1.5rem 1.5rem;
  display: flex;
  justify-content: center;
  gap: 1rem;
`;

const Button = styled.button`
    width: 100%;
    box-sizing: border-box;
    max-width: 100%;
    background: linear-gradient(90deg, var(--hn-green, #D1FF03), var(--hn-yellow, #FECF38));
    color: var(--hn-black, #001413);
    border: none;
    font-weight: 700;
    font-size: 1rem;
    padding: 16px 16px;
    border-radius: 8px;
    box-shadow: 0 0 7px 0 var(--hn-green, #D1FF03);
    transition: transform 0.09s, box-shadow 0.09s;
    letter-spacing: 0.5px;
    text-align: center;
    cursor: pointer;
    margin-top: 12px;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.1);
  }
`;


const RequirementsPopup = ({ isOpen = true, imageSrc, modulesCount = {}, goTo }) => {
  const { authenticated = false, user, handleLogout, loadingAuthState } = useContext(UserContext);

  const [checkedItems, setCheckedItems] = useState([]);
  const [showCofetty, setShowConfetti] = useState(true);

  const requirements = [
    `Completed all ${modulesCount?.modules} course modules`,
    `Worked on ${modulesCount?.assignments} assignment`,
    `Aswered ${modulesCount?.multipleChoice} multiple choices/quiz`,
    `Viewed ${modulesCount?.resources} resources`,
    `Watched ${modulesCount?.videos} videos`,
    `Earned ${modulesCount?.modules} Badges`,
    "Earned 1 unique certificate",
  ];

  useEffect(() => {
    if (isOpen) {
      setCheckedItems([]);

      requirements.forEach((_, index) => {
        setTimeout(() => {
          setCheckedItems(prev => [...prev, index]);
        }, 600 * (index + 1));
      });
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Overlay>
      <PopupContainer onClick={(e) => e.stopPropagation()}>
        <Header>
          <BadgeImage src={imageSrc} alt="Badge" />
          <Title>Congratulations, @{user?.handle || "hacker"}! 🎉</Title>
          <Subtitle>All Modules completed</Subtitle>
        </Header>

        <RequirementsList>
          {requirements?.map((requirement, index) => (
            <RequirementItem key={index} checked={checkedItems.includes(index)}>
              <Checkbox checked={checkedItems.includes(index)}>
                {checkedItems.includes(index) && (
                  // <Check size={16} color="white" />
                  <i class="hn hn-check-circle-solid"></i>
                )}
              </Checkbox>
              <RequirementText checked={checkedItems.includes(index)}>
                {requirement}
              </RequirementText>
            </RequirementItem>
          ))}
        </RequirementsList>
        <ButtonContainer>
          <Button onClick={() => goTo('/my-certificate')}>
            View Certificate
          </Button>
          <Button onClick={() => goTo('/my-courses')}>
            Back To Courses
          </Button>
        </ButtonContainer>
      </PopupContainer>
      <ConfettiCelebration trigger={showCofetty} onDone={() => setShowConfetti(false)} />

    </Overlay>
  );
};

export default RequirementsPopup;